<template>
  <div class="login-page">
    <div class="login-container">
      <!-- Left Panel - Branding -->
      <div class="brand-panel">
        <div class="brand-content">
          <div class="logo-box">
            <img src="@/assets/logo.png" alt="ZENTRY" class="logo" />
          </div>
          
          <p class="brand-subtitle">Automatiza, analiza y optimiza tu contabilidad con IA</p>
          
          <div class="features">
            <div class="feature">
              <div class="feature-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 11L12 14L22 4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  <path d="M21 12V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3H16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </div>
              <div class="feature-text">
                <h3>Descarga Automática</h3>
                <p>Sincronización directa con el SRI</p>
              </div>
            </div>
            
            <div class="feature">
              <div class="feature-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  <path d="M2 17L12 22L22 17M2 12L12 17L22 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </div>
              <div class="feature-text">
                <h3>Organización Inteligente</h3>
                <p>ZENTRY clasifica tus documentos</p>
              </div>
            </div>
            
            <div class="feature">
              <div class="feature-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="3" width="7" height="7" stroke="currentColor" stroke-width="2"/>
                  <rect x="14" y="3" width="7" height="7" stroke="currentColor" stroke-width="2"/>
                  <rect x="14" y="14" width="7" height="7" stroke="currentColor" stroke-width="2"/>
                  <rect x="3" y="14" width="7" height="7" stroke="currentColor" stroke-width="2"/>
                </svg>
              </div>
              <div class="feature-text">
                <h3>Dashboard Avanzado</h3>
                <p>Análisis y reportes en tiempo real</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Panel - Login Form -->
      <div class="form-panel">
        <div class="form-wrapper">
          <div class="form-header">
            <h2>Iniciar Sesión</h2>
            <p>Ingresa tus credenciales para acceder</p>
          </div>

          <form @submit.prevent="handleLogin" class="login-form">
            <div class="form-group">
              <label for="email">Correo electrónico</label>
              <input
                id="email"
                v-model="email"
                type="email"
                placeholder="ejemplo@empresa.com"
                required
                :class="{ 'error': errors.email }"
              >
              <span v-if="errors.email" class="error-message">{{ errors.email }}</span>
            </div>

            <div class="form-group">
              <label for="password">Contraseña</label>
              <div class="password-input">
                <input
                  id="password"
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  placeholder="Tu contraseña"
                  required
                  :class="{ 'error': errors.password }"
                >
                <button
                  type="button"
                  class="password-toggle"
                  @click="showPassword = !showPassword"
                >
                  <svg v-if="showPassword" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20C7 20 2.73 16.39 1 12A18.45 18.45 0 0 1 5.06 5.06L17.94 17.94Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M1 1L23 23" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4C17 4 21.27 7.61 23 12A18.5 18.5 0 0 1 19.9 16.76" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2"/>
                  </svg>
                </button>
              </div>
              <span v-if="errors.password" class="error-message">{{ errors.password }}</span>
            </div>

            <div class="form-options">
              <label class="checkbox-label">
                <input type="checkbox" v-model="rememberMe">
                <span class="checkmark"></span>
                Recordar sesión
              </label>
              <router-link to="/forgot-password" class="forgot-password">¿Olvidaste tu contraseña?</router-link>
            </div>

            <!-- Error general -->
            <div v-if="errors.general" class="alert alert-error" style="margin-bottom: 20px; background: #fee2e2; border: 1px solid #fecaca; color: #dc2626; padding: 12px; border-radius: 6px; font-weight: 500;">
              <strong>⚠️ Error:</strong> {{ errors.general }}
            </div>

            <!-- Email no verificado -->
            <div v-if="errors.email_not_verified" class="alert alert-warning" style="margin-bottom: 20px; background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); border: 1px solid #f59e0b; color: #92400e; padding: 16px; border-radius: 8px;">
              <div style="display: flex; align-items: center; margin-bottom: 12px;">
                <span style="font-size: 1.5rem; margin-right: 8px;">📧</span>
                <strong>Verificación de Email Pendiente</strong>
              </div>
              <p style="margin: 0 0 12px 0; line-height: 1.4;">
                Tu cuenta aún no ha sido verificada. Por favor, revisa tu bandeja de entrada y haz clic en el enlace de verificación que te enviamos.
              </p>
              <p style="margin: 0 0 16px 0; font-size: 0.9rem; opacity: 0.8;">
                Si no encuentras el correo, revisa tu carpeta de spam o solicita un nuevo enlace de verificación.
              </p>
              <div style="display: flex; gap: 12px; align-items: center; flex-wrap: wrap;">
                <button 
                  @click="resendVerificationEmail" 
                  class="btn-resend"
                  :disabled="resendLoading || resendCooldown > 0"
                  style="background: #f59e0b; color: white; border: none; padding: 10px 20px; border-radius: 6px; font-weight: 600; cursor: pointer; transition: all 0.2s ease;"
                >
                  <span v-if="resendLoading">Enviando...</span>
                  <span v-else-if="resendCooldown > 0">Espera {{ resendCooldown }}s</span>
                  <span v-else>📨 Reenviar Email</span>
                </button>
                <span v-if="resendSuccess" style="color: #15803d; font-size: 0.9rem; font-weight: 500;">✅ Email enviado</span>
              </div>
            </div>

            <!-- Cuenta pendiente de aprobación -->
            <div v-if="errors.account_pending" class="alert alert-info" style="margin-bottom: 20px; background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%); border: 1px solid #3b82f6; color: #1e40af; padding: 16px; border-radius: 8px;">
              <div style="display: flex; align-items: center; margin-bottom: 12px;">
                <span style="font-size: 1.5rem; margin-right: 8px;">⏳</span>
                <strong>Cuenta Pendiente de Aprobación</strong>
              </div>
              <p style="margin: 0 0 12px 0; line-height: 1.4;">
                ¡Gracias por verificar tu email! Tu cuenta está siendo revisada por nuestro equipo de administración.
              </p>
              <p style="margin: 0; font-size: 0.9rem; opacity: 0.8;">
                📋 Te notificaremos por email cuando tu cuenta sea aprobada y puedas acceder al sistema. Este proceso generalmente toma entre 24-48 horas.
              </p>
            </div>

            <!-- Cuenta suspendida -->
            <div v-if="errors.account_suspended" class="alert alert-danger" style="margin-bottom: 20px; background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%); border: 1px solid #ef4444; color: #991b1b; padding: 16px; border-radius: 8px;">
              <div style="display: flex; align-items: center; margin-bottom: 12px;">
                <span style="font-size: 1.5rem; margin-right: 8px;">🚫</span>
                <strong>Cuenta Suspendida</strong>
              </div>
              <p style="margin: 0 0 12px 0; line-height: 1.4;">
                Tu cuenta ha sido suspendida. Si crees que esto es un error, por favor contacta a soporte.
              </p>
              <p style="margin: 0; font-size: 0.9rem; opacity: 0.8;">
                📧 Puedes escribirnos a: <a href="mailto:soporte@zentry.app" style="color: #991b1b; text-decoration: underline;">soporte@zentry.app</a>
              </p>
            </div>

            <!-- Mensaje promocional para email no registrado -->
            <div v-if="errors.email_not_found" class="alert alert-promotional" style="margin-bottom: 20px; background: linear-gradient(135deg, #0891b2 0%, #0e7490 100%); border: 1px solid #06b6d4; color: white; padding: 16px; border-radius: 8px; font-weight: 500;">
              <div style="display: flex; align-items: center; margin-bottom: 12px;">
                <span style="font-size: 1.5rem; margin-right: 8px;">🚀</span>
                <strong>¡Únete a ZENTRY APP!</strong>
              </div>
              <p style="margin: 0 0 12px 0; line-height: 1.4;">
                {{ errors.promotional_message }}
              </p>
              <p style="margin: 0 0 16px 0; font-size: 0.9rem; opacity: 0.9;">
                Descubre el poder de la inteligencia artificial aplicada a tus documentos del SRI. 
                Automatiza tu contabilidad y toma decisiones inteligentes con nuestro sistema.
              </p>
              <div style="display: flex; gap: 12px; align-items: center;">
                <button 
                  @click="goToRegister" 
                  class="btn-register-promo"
                  style="background: white; color: #0891b2; border: none; padding: 10px 20px; border-radius: 6px; font-weight: 600; cursor: pointer; transition: all 0.2s ease;"
                >
                  📝 Registrarme Ahora
                </button>
                <span style="font-size: 0.85rem; opacity: 0.8;">¡Es gratis empezar!</span>
              </div>
            </div>

            <button type="submit" class="btn-primary" :disabled="loading">
              <span v-if="loading" class="spinner"></span>
              {{ loading ? 'Iniciando...' : 'Iniciar Sesión' }}
            </button>
          </form>

          <div class="form-footer">
            <p>¿No tienes cuenta? <router-link to="/register" class="signup-link">Crear cuenta</router-link></p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from '@/stores/auth'
import { authService } from '@/services/api'

export default {
  name: 'Login',
  data() {
    return {
      email: '',
      password: '',
      showPassword: false,
      rememberMe: false,
      loading: false,
      resendLoading: false,
      resendCooldown: 0,
      resendSuccess: false,
      errors: {}
    }
  },
  mounted() {
    if (this.$route.query.email) {
      this.email = this.$route.query.email
    }
  },
  setup() {
    const authStore = useAuthStore()
    return { authStore }
  },
  methods: {
    async handleLogin() {

      this.errors = {}
      this.resendSuccess = false
      
      // Validación básica
      if (!this.email) {
        this.errors.email = 'El correo electrónico es requerido'

        return
      }
      
      if (!this.password) {
        this.errors.password = 'La contraseña es requerida'

        return
      }

      this.loading = true


      try {
        const credentials = {
          email: this.email,
          password: this.password,
          remember_me: this.rememberMe
        }
        

        
        const response = await this.authStore.login(credentials)

        
        if (response.success) {
          // Mostrar mensaje de éxito

          
          // Redirigir al destino original si existe, sino al dashboard
          const redirectPath = this.$route.query.redirect || '/dashboard'
          this.$router.push(redirectPath)
        } else {
          // Mostrar errores específicos

          
          // Limpiar todos los estados de error
          this.errors = {
            email_not_found: false,
            email_not_verified: false,
            account_pending: false,
            account_suspended: false,
            general: ''
          }
          
          // Verificar el tipo de error específico
          switch (response.error_type) {
            case 'email_not_found':
              this.errors.email_not_found = true
              this.errors.promotional_message = response.message
              break
              
            case 'email_not_verified':
              this.errors.email_not_verified = true
              break
              
            case 'account_pending_approval':
              this.errors.account_pending = true
              break
              
            case 'account_suspended':
              this.errors.account_suspended = true
              break
              
            default:
              // Para otros errores (password incorrecto, etc.)
              this.errors.general = response.message || 'Error al iniciar sesión'
          }
          

          
          // Si hay errores específicos de campos, agregarlos también
          if (response.errors && Object.keys(response.errors).length > 0) {
            Object.assign(this.errors, response.errors)

          }
          

        }
      } catch (error) {
        console.error('💀 Error en login:', error)
        this.errors.general = 'Error de conexión. Inténtalo de nuevo.'
      } finally {
        this.loading = false

      }
    },
    
    // Método para redirigir al registro
    goToRegister() {
      this.$router.push('/register')
    },
    
    // Método para reenviar email de verificación
    async resendVerificationEmail() {
      if (this.resendLoading || this.resendCooldown > 0) return
      
      this.resendLoading = true
      this.resendSuccess = false
      
      try {
        const response = await authService.resendVerificationEmail(this.email)
        
        if (response.success) {
          this.resendSuccess = true
          // Iniciar cooldown de 60 segundos
          this.resendCooldown = 60
          const interval = setInterval(() => {
            this.resendCooldown--
            if (this.resendCooldown <= 0) {
              clearInterval(interval)
            }
          }, 1000)
        } else {
          this.errors.general = response.message || 'Error al reenviar el email'
        }
      } catch (error) {
        console.error('Error al reenviar email:', error)
        this.errors.general = 'Error de conexión. Inténtalo de nuevo.'
      } finally {
        this.resendLoading = false
      }
    }
  }
}
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.login-page {
  height: 100vh;
  width: 100vw;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%);
  display: flex;
  align-items: stretch;
  justify-content: center;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.login-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  height: 100%;
  background: white;
  overflow: hidden;
}

/* Brand Panel */
.brand-panel {
  background: linear-gradient(135deg, #06b6d4 0%, #0891b2 50%, #0e7490 100%);
  padding: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  position: relative;
  overflow: hidden;
}

.brand-panel::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
  animation: pulse 15s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.1); opacity: 0.8; }
}

.brand-content {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 500px;
  text-align: center;
}

.logo-box {
  background: white;
  padding: 20px 30px;
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  margin-bottom: 24px;
  display: inline-block;
  transition: transform 0.3s ease;
}

.logo-box:hover {
  transform: translateY(-5px);
}

.logo {
  max-width: 30vw;
  height: auto;
  display: block;
}

.brand-title {
  font-size: 1.65rem;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 10px;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.brand-subtitle {
  font-size: 0.95rem;
  opacity: 0.95;
  line-height: 1.4;
  margin-bottom: 28px;
}

.features {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.feature {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  text-align: left;
}

.feature:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateX(10px);
}

.feature-icon {
  width: 40px;
  height: 40px;
  min-width: 40px;
  background: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0891b2;
}

.feature-icon svg {
  width: 20px;
  height: 20px;
}

.feature-text h3 {
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 3px;
}

.feature-text p {
  font-size: 0.8rem;
  opacity: 0.9;
  line-height: 1.3;
}

/* Form Panel */
.form-panel {
  padding: 30px 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  overflow: hidden;
}

.form-wrapper {
  width: 100%;
  max-width: 450px;
}

.form-header {
  margin-bottom: 24px;
  text-align: center;
}

.form-header h2 {
  font-size: 1.75rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 6px;
}

.form-header p {
  color: #64748b;
  font-size: 0.9rem;
}

.login-form {
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 6px;
  font-size: 0.85rem;
  letter-spacing: 0.3px;
}

.form-group input {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  background: #f8fafc;
  color: #0f172a;
  font-family: inherit;
}

.form-group input:focus {
  outline: none;
  border-color: #0891b2;
  background: white;
  box-shadow: 0 0 0 4px rgba(8, 145, 178, 0.1);
  transform: translateY(-2px);
}

.form-group input::placeholder {
  color: #94a3b8;
}

.form-group input.error {
  border-color: #ef4444;
  background: #fef2f2;
}

.password-input {
  position: relative;
}

.password-toggle {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #64748b;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.password-toggle:hover {
  color: #0891b2;
  background: #f1f5f9;
}

.error-message {
  color: #ef4444;
  font-size: 0.8rem;
  margin-top: 4px;
  display: block;
  font-weight: 500;
}

.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 0.85rem;
  color: #475569;
  font-weight: 500;
}

.checkbox-label input {
  margin-right: 8px;
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #0891b2;
}

.forgot-password {
  color: #0891b2;
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 600;
  transition: all 0.2s ease;
}

.forgot-password:hover {
  color: #0e7490;
  text-decoration: underline;
}

.alert {
  padding: 12px 16px;
  border-radius: 10px;
  margin-bottom: 16px;
  font-size: 0.85rem;
  font-weight: 500;
  border: 1px solid;
}

.alert-error {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border-color: #fca5a5;
  color: #991b1b;
}

.alert-promotional {
  background: linear-gradient(135deg, #0891b2 0%, #0e7490 100%);
  border-color: #06b6d4;
  color: white;
}

.btn-primary {
  width: 100%;
  padding: 13px 24px;
  background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(6, 182, 212, 0.4);
  letter-spacing: 0.3px;
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #0891b2 0%, #0e7490 100%);
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(6, 182, 212, 0.5);
}

.btn-primary:active:not(:disabled) {
  transform: translateY(-1px);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2.5px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-right: 10px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.form-footer {
  text-align: center;
  padding-top: 16px;
  margin-top: 16px;
  border-top: 2px solid #f1f5f9;
}

.form-footer p {
  color: #64748b;
  margin: 0;
  font-size: 0.85rem;
}

.signup-link {
  color: #0891b2;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.2s ease;
  border-bottom: 2px solid transparent;
}

.signup-link:hover {
  color: #0e7490;
  border-bottom-color: #0e7490;
}

.btn-register-promo {
  background: white !important;
  color: #0891b2 !important;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  font-size: 0.9rem;
}

.btn-register-promo:hover {
  background: #f0fdfa !important;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
}

/* Responsive Design */
@media (max-width: 1024px) {
  .brand-title {
    font-size: 1.65rem;
  }
  
  .brand-subtitle {
    font-size: 0.95rem;
  }
  
  .form-header h2 {
    font-size: 1.65rem;
  }
}

@media (max-width: 968px) {
  .login-container {
    grid-template-columns: 1fr;
  }
  
  .brand-panel {
    display: none;
  }
  
  .form-panel {
    padding: 40px 30px;
  }
}

@media (max-width: 640px) {
  .form-panel {
    padding: 30px 24px;
  }
  
  .form-header {
    margin-bottom: 24px;
  }
  
  .form-header h2 {
    font-size: 1.5rem;
  }
  
  .form-header p {
    font-size: 0.875rem;
  }
  
  .form-group {
    margin-bottom: 18px;
  }
  
  .form-options {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .btn-primary {
    padding: 13px 20px;
    font-size: 0.95rem;
  }
}

@media (max-width: 480px) {
  .form-panel {
    padding: 24px 20px;
  }
  
  .form-header h2 {
    font-size: 1.35rem;
  }
  
  .logo {
    max-width: 150px;
  }
}
</style>